import React from "react";
import { useAuth } from "../contexts/AuthContext";
import { apiRequest, ApiError } from "../api/client";
import { DashboardLayout } from "../components/dashboard/DashboardLayout";
import AnalysisSubnav from "../components/dashboard/AnalysisSubnav";
import { MONTH_NAMES } from "../constants/dates";
import { formatCurrency } from "../utils/currency";
import { Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

interface MonthlySummaryItem {
  month: number;
  income: number;
  expense: number;
  net: number;
}

type CategorySummaryKind = "income" | "expense" | "transfer" | "uncategorized";

interface CategorySummary {
  categoryId: number | null;
  categoryName: string;
  color: string | null;
  kind: CategorySummaryKind;
  total: number;
}

interface MonthlyCategorySummary extends CategorySummary {
  month: number;
}

interface CategorySummaryResponse {
  yearly: CategorySummary[];
  monthly: MonthlyCategorySummary[];
}

interface CategoryBreakdownTransaction {
  id: number;
  transactionDate: string;
  description: string | null;
  amount: number;
}

interface CategoryBreakdownResponse {
  year: number;
  month: number;
  categoryId: number | null;
  transactions: CategoryBreakdownTransaction[];
}

type CategoryMatrixMode = "income" | "expense";

interface CategoryMatrixRow {
  categoryId: number | null;
  categoryName: string;
  categoryColor: string | null;
  sourceKind: CategorySummaryKind;
  mode: CategoryMatrixMode;
  monthly: number[];
  total: number;
}

interface CategoryDrawerState {
  key: string;
  categoryName: string;
  categoryColor: string | null;
  mode: CategoryMatrixMode;
  month: number;
  value: number;
  loading: boolean;
  transactions: CategoryBreakdownTransaction[] | null;
  error: string | null;
}

function normalizeZero(value: number) {
  return Math.abs(value) < 0.005 ? 0 : value;
}

function formatDateLabel(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("es-ES");
}

function extractErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return fallback;
}

const DEFAULT_CATEGORY_COLOR = "#cbd5f5";
const COLOR_FALLBACK_KEY = "zzzzzzzz";

function getColorSortKey(color: string | null) {
  return color ? color.toLowerCase() : COLOR_FALLBACK_KEY;
}

function sortCategoriesByColor<T extends { categoryColor: string | null }>(
  rows: T[],
  compareWithinColor: (a: T, b: T) => number
) {
  return rows.sort((a, b) => {
    const colorDiff = getColorSortKey(a.categoryColor).localeCompare(getColorSortKey(b.categoryColor));
    if (colorDiff !== 0) return colorDiff;
    return compareWithinColor(a, b);
  });
}

export default function AnnualOverviewPage() {
  const { token, logout } = useAuth();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(currentYear);
  const [monthlySummary, setMonthlySummary] = React.useState<MonthlySummaryItem[]>([]);
  const [categorySummaryMonthly, setCategorySummaryMonthly] = React.useState<MonthlyCategorySummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const breakdownCacheRef = React.useRef<Map<string, CategoryBreakdownTransaction[]>>(new Map());
  const [categoryDrawer, setCategoryDrawer] = React.useState<CategoryDrawerState | null>(null);
  const drawerHeadingId = React.useId();
  const drawerSubtitleId = React.useId();
  const drawerTotalId = React.useId();

  const years = React.useMemo(
    () => Array.from({ length: 6 }, (_, index) => currentYear - index),
    [currentYear]
  );

  const handleRequest = React.useCallback(
    async <T,>(promise: Promise<T>): Promise<T> => {
      try {
        return await promise;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          logout();
        }
        throw err;
      }
    },
    [logout]
  );

  const loadData = React.useCallback(
    async (selectedYear: number) => {
      if (!token) return;
      setLoading(true);
      setError(null);
      try {
        const [monthly, categoryBreakdown] = await Promise.all([
          handleRequest(
            apiRequest<MonthlySummaryItem[]>(`/transactions/summary/monthly?year=${selectedYear}`, {
              token,
            })
          ),
          handleRequest(
            apiRequest<CategorySummaryResponse>(`/transactions/summary/categories?year=${selectedYear}`, {
              token,
            })
          ),
        ]);
        setMonthlySummary(monthly);
        setCategorySummaryMonthly(categoryBreakdown.monthly);
        breakdownCacheRef.current.clear();
        setCategoryDrawer(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : "No pudimos obtener el resumen anual";
        setError(message);
        setMonthlySummary([]);
        setCategorySummaryMonthly([]);
        breakdownCacheRef.current.clear();
        setCategoryDrawer(null);
      } finally {
        setLoading(false);
      }
    },
    [handleRequest, token]
  );

  React.useEffect(() => {
    if (!token) return;
    loadData(year);
  }, [token, year, loadData]);

  const monthsMatrix = React.useMemo(() => {
    const summaryByMonth = new Map(monthlySummary.map(item => [item.month, item]));
    let cumulative = 0;
    return Array.from({ length: 12 }, (_, index) => {
      const month = index + 1;
      const entry = summaryByMonth.get(month);
      const income = entry?.income ?? 0;
      const expense = entry?.expense ?? 0;
      const net = entry?.net ?? 0;
      cumulative += net;
      return {
        month,
        income,
        expense,
        net,
        cumulative,
      };
    });
  }, [monthlySummary]);

  const monthlyChartData = React.useMemo(
    () =>
      monthsMatrix.map(item => ({
        month: MONTH_NAMES[item.month - 1].slice(0, 3),
        income: normalizeZero(item.income),
        expenses: normalizeZero(item.expense),
        balance: normalizeZero(item.net),
      })),
    [monthsMatrix]
  );

  const handleYearChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextYear = Number(event.target.value);
    setYear(nextYear);
  };

  const { incomeCategoryMatrix, expenseCategoryMatrix } = React.useMemo(() => {
    const ensureRow = (
      map: Map<string, CategoryMatrixRow>,
      entry: MonthlyCategorySummary,
      mode: CategoryMatrixMode
    ) => {
      const key = String(entry.categoryId ?? "uncategorized");
      if (!map.has(key)) {
        map.set(key, {
          categoryId: entry.categoryId,
          categoryName: entry.categoryName,
          categoryColor: entry.color ?? null,
          sourceKind: entry.kind,
          mode,
          monthly: Array.from({ length: 12 }, () => 0),
          total: 0,
        });
      }
      return map.get(key)!;
    };

    const incomeMap = new Map<string, CategoryMatrixRow>();
    const expenseMap = new Map<string, CategoryMatrixRow>();
    const incomeSums = new Map<string, number>();

    for (const entry of categorySummaryMonthly) {
      const currentTotal = entry.total;
      if (normalizeZero(currentTotal) === 0) continue;

      let bucket: "income" | "expense" | null;
      if (entry.kind === "income") {
        bucket = "income";
      } else if (entry.kind === "expense") {
        bucket = "expense";
      } else if (entry.kind === "transfer") {
        bucket = currentTotal >= 0 ? "income" : "expense";
      } else if (entry.kind === "uncategorized") {
        bucket = currentTotal >= 0 ? "income" : "expense";
      } else {
        bucket = null;
      }

      if (bucket === "income") {
        const key = String(entry.categoryId ?? "uncategorized");
        const row = ensureRow(incomeMap, entry, "income");
        row.monthly[entry.month - 1] = currentTotal;
        row.total += currentTotal;
        incomeSums.set(key, (incomeSums.get(key) ?? 0) + currentTotal);
      } else if (bucket === "expense") {
        const row = ensureRow(expenseMap, entry, "expense");
        row.monthly[entry.month - 1] = currentTotal;
        row.total += currentTotal;
      }
    }

    const incomeCategoryMatrix = sortCategoriesByColor(
      Array.from(incomeMap.values()).filter(row => {
        const key = String(row.categoryId ?? "uncategorized");
        return normalizeZero(incomeSums.get(key) ?? 0) > 0;
      }),
      (a, b) => b.total - a.total
    );
    const expenseCategoryMatrix = sortCategoriesByColor(
      Array.from(expenseMap.values()),
      (a, b) => Math.abs(b.total) - Math.abs(a.total)
    );

    return { incomeCategoryMatrix, expenseCategoryMatrix };
  }, [categorySummaryMonthly]);

  const categoryPieData = React.useMemo(
    () =>
      expenseCategoryMatrix
        .filter(row => normalizeZero(row.total) !== 0)
        .slice(0, 12)
        .map(row => ({
          name: row.categoryName,
          value: Math.abs(normalizeZero(row.total)),
          color: row.categoryColor ?? DEFAULT_CATEGORY_COLOR,
        })),
    [expenseCategoryMatrix]
  );

  const monthlyIncomeTotals = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) =>
        incomeCategoryMatrix.reduce((sum, row) => sum + normalizeZero(row.monthly[monthIndex]), 0)
      ),
    [incomeCategoryMatrix]
  );

  const monthlyExpenseTotals = React.useMemo(
    () =>
      Array.from({ length: 12 }, (_, monthIndex) =>
        expenseCategoryMatrix.reduce((sum, row) => sum + Math.abs(normalizeZero(row.monthly[monthIndex])), 0)
      ),
    [expenseCategoryMatrix]
  );

  const displayedIncomeTotal = React.useMemo(
    () => incomeCategoryMatrix.reduce((sum, row) => sum + normalizeZero(row.total), 0),
    [incomeCategoryMatrix]
  );

  const displayedExpenseTotal = React.useMemo(
    () => expenseCategoryMatrix.reduce((sum, row) => sum + Math.abs(normalizeZero(row.total)), 0),
    [expenseCategoryMatrix]
  );

  const breakdownAlignedTotals = React.useMemo(
    () => ({
      income: displayedIncomeTotal,
      expense: displayedExpenseTotal,
      balance: displayedIncomeTotal - displayedExpenseTotal,
    }),
    [displayedIncomeTotal, displayedExpenseTotal]
  );

  const categoryRows = React.useMemo(
    () =>
      [
        ...incomeCategoryMatrix.map(row => ({ ...row, type: "income" as const })),
        ...expenseCategoryMatrix.map(row => ({ ...row, type: "expense" as const })),
      ],
    [incomeCategoryMatrix, expenseCategoryMatrix]
  );

  const handleCategoryCellToggle = React.useCallback(
    (row: CategoryMatrixRow, monthIndex: number) => {
      if (!token) return;
      const rawValue = row.monthly[monthIndex];
      if (normalizeZero(rawValue) === 0) return;

      const monthNumber = monthIndex + 1;
      const breakdownMode = row.sourceKind === "transfer" ? "transfer" : row.mode;
      const key = `${year}-${monthNumber}-${row.categoryId ?? "uncategorized"}-${breakdownMode}`;
      const isSame = categoryDrawer && categoryDrawer.key === key;

      if (isSame) {
        setCategoryDrawer(null);
        return;
      }

      const drawerBase: CategoryDrawerState = {
        key,
        categoryName: row.categoryName,
        categoryColor: row.categoryColor ?? null,
        mode: row.mode,
        month: monthNumber,
        value: rawValue,
        loading: true,
        transactions: null,
        error: null,
      };

      const cached = breakdownCacheRef.current.get(key);
      if (cached) {
        setCategoryDrawer({
          ...drawerBase,
          loading: false,
          transactions: cached,
          value: rawValue,
        });
        return;
      }

      setCategoryDrawer(drawerBase);

      handleRequest(
        apiRequest<CategoryBreakdownResponse>(
          `/transactions/summary/categories/breakdown?year=${year}&month=${monthNumber}&categoryId=${
            row.categoryId ?? "uncategorized"
          }&mode=${breakdownMode}`,
          { token }
        )
      )
        .then(response => {
          breakdownCacheRef.current.set(key, response.transactions);
          setCategoryDrawer(prev =>
            prev && prev.key === key
              ? {
                  ...prev,
                  loading: false,
                  transactions: response.transactions,
                  value: rawValue,
                  error: null,
                }
              : prev
          );
        })
        .catch(err => {
          const message = extractErrorMessage(err, "No pudimos obtener los movimientos");
          setCategoryDrawer(prev =>
            prev && prev.key === key
              ? {
                  ...prev,
                  loading: false,
                  transactions: null,
                  error: message,
                }
              : prev
          );
        });
    },
    [token, year, handleRequest, categoryDrawer]
  );

  const handleDrawerClose = React.useCallback(() => {
    setCategoryDrawer(null);
  }, []);

  React.useEffect(() => {
    if (!categoryDrawer) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setCategoryDrawer(null);
      }
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [categoryDrawer]);

  return (
    <DashboardLayout active="insights">
      <div className="analysis-page-header">
        <div className="analysis-page-header-inner">
          <div className="analysis-page-heading">
            <h1>Análisis y resultados</h1>
            <p>Consulta tus ingresos, gastos y tendencias financieras</p>
          </div>
          <label className="analysis-year-filter">
            <span>Año</span>
            <select className="input" value={year} onChange={handleYearChange}>
              {years.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="analysis-page-subnav">
        <div className="analysis-page-subnav-inner">
          <AnalysisSubnav mode="accounts" />
        </div>
      </div>

      <div className="dashboard-page-content analysis-page-content grid" style={{ gap: "1.5rem" }}>
        <section className="analysis-kpi-strip">
          <div className="cards-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
            <article className="card">
              <strong>Total ingresos {year}</strong>
              <span className="stat-value positive">{formatCurrency(normalizeZero(breakdownAlignedTotals.income))}</span>
            </article>
            <article className="card">
              <strong>Total gastos {year}</strong>
              <span className="stat-value negative">{formatCurrency(normalizeZero(-breakdownAlignedTotals.expense))}</span>
            </article>
            <article className="card">
              <strong>Balance anual</strong>
              <span className={`stat-value ${breakdownAlignedTotals.balance >= 0 ? "positive" : "negative"}`}>
                {formatCurrency(normalizeZero(breakdownAlignedTotals.balance))}
              </span>
            </article>
          </div>
        </section>

        <div className="analysis-charts-grid">
          <section className="panel">
            <div className="panel-header">
              <h3 className="panel-title">Evolución mensual</h3>
            </div>
            <div style={{ width: "100%", height: "260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={monthlyChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="income" name="Ingresos" fill="#10b981" />
                  <Bar dataKey="expenses" name="Gastos" fill="#ef4444" />
                  <Line dataKey="balance" name="Ahorro" stroke="#8b5cf6" strokeWidth={2.5} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="panel">
            <div className="panel-header">
              <h3 className="panel-title">Ingresos y gastos por categorías</h3>
            </div>
            {loading ? (
              <div className="loader" style={{ alignSelf: "flex-start" }}>
                <div className="spinner" />
                Preparando categorías...
              </div>
            ) : error ? (
              <div className="empty-state">{error}</div>
            ) : categoryPieData.length ? (
              <div style={{ width: "100%", height: "260px" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryPieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ percent }) => `${((percent ?? 0) * 100).toFixed(0)}%`}
                    >
                      {categoryPieData.map((entry, index) => (
                        <Cell key={`pie-cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => formatCurrency(-Math.abs(value))} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="empty-state">No hay datos de categorías para este año.</div>
            )}
          </section>
        </div>

        <section className="panel">
          <div className="panel-header">
            <h3 className="panel-title">Resultados por categorías</h3>
          </div>
          {loading ? (
            <div className="loader" style={{ alignSelf: "flex-start" }}>
              <div className="spinner" />
              Preparando categorías...
            </div>
          ) : error ? (
            <div className="empty-state">{error}</div>
          ) : categoryRows.length ? (
            <div style={{ display: "grid", gap: "1rem" }}>
              <div className="annual-matrix-wrapper">
                <table className="annual-matrix compact analysis-results-table" role="grid">
                  <thead>
                    <tr>
                      <th scope="col">Resultados</th>
                      {MONTH_NAMES.map(monthName => (
                        <th key={`totals-head-${monthName}`} scope="col">
                          {monthName.slice(0, 3)}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="analysis-results-row analysis-results-row-income">
                      <th scope="row">TOTAL INGRESOS</th>
                      {monthlyIncomeTotals.map((amount, index) => (
                        <td key={`total-income-${index}`} className="positive">
                          {amount ? formatCurrency(amount) : "—"}
                        </td>
                      ))}
                      <td className="positive">{formatCurrency(displayedIncomeTotal)}</td>
                    </tr>
                    <tr className="analysis-results-row analysis-results-row-expense">
                      <th scope="row">TOTAL GASTOS</th>
                      {monthlyExpenseTotals.map((amount, index) => (
                        <td key={`total-expense-${index}`} className="negative">
                          {amount ? formatCurrency(-amount) : "—"}
                        </td>
                      ))}
                      <td className="negative">{formatCurrency(-displayedExpenseTotal)}</td>
                    </tr>
                    <tr className="analysis-results-row analysis-results-row-balance">
                      <th scope="row">BALANCE</th>
                      {monthlyIncomeTotals.map((income, index) => {
                        const balance = income - monthlyExpenseTotals[index];
                        return (
                          <td key={`total-balance-${index}`} className={balance >= 0 ? "positive" : "negative"}>
                            {balance ? formatCurrency(balance) : "—"}
                          </td>
                        );
                      })}
                      <td className={displayedIncomeTotal - displayedExpenseTotal >= 0 ? "positive" : "negative"}>
                        {formatCurrency(displayedIncomeTotal - displayedExpenseTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="annual-matrix-wrapper">
                <table className="annual-matrix compact" role="grid">
                  <thead>
                    <tr>
                      <th scope="col">Categoría</th>
                      {MONTH_NAMES.map(monthName => (
                        <th key={`cat-head-${monthName}`} scope="col">
                          {monthName.slice(0, 3)}
                        </th>
                      ))}
                      <th scope="col">Total</th>
                      <th scope="col">% del total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.map((row, rowIndex) => {
                      const denominator = row.type === "income" ? displayedIncomeTotal : displayedExpenseTotal;
                      const rowTotal = row.type === "income" ? normalizeZero(row.total) : Math.abs(normalizeZero(row.total));
                      const percentage = denominator > 0 ? (rowTotal / denominator) * 100 : 0;
                      const showExpenseDivider = rowIndex > 0 && row.type === "expense" && categoryRows[rowIndex - 1].type === "income";
                      return (
                        <React.Fragment key={`category-fragment-${row.mode}-${row.categoryId ?? "uncategorized"}`}>
                          {showExpenseDivider ? (
                            <tr className="analysis-category-separator" aria-hidden="true">
                              <td colSpan={MONTH_NAMES.length + 3}>Gastos</td>
                            </tr>
                          ) : null}
                          <tr key={`category-row-${row.mode}-${row.categoryId ?? "uncategorized"}`}>
                            <th scope="row">
                              <span className="category-color-label">
                                <span
                                  className="category-color-dot"
                                  style={{ backgroundColor: row.categoryColor ?? DEFAULT_CATEGORY_COLOR }}
                                  aria-hidden="true"
                                />
                                <span>{row.categoryName}</span>
                              </span>
                            </th>
                            {row.monthly.map((value, index) => {
                              const normalized = normalizeZero(value);
                              const displayValue = row.type === "income" ? normalized : Math.abs(normalized);
                              const hasData = displayValue !== 0;
                              return (
                                <td
                                  key={`cat-cell-${row.mode}-${row.categoryId ?? "uncategorized"}-${index}`}
                                  className={row.type === "income" ? "positive" : "negative"}
                                  style={{
                                    cursor: hasData ? "pointer" : undefined,
                                    color: "#2c3e50",
                                  }}
                                  onClick={() => handleCategoryCellToggle(row, index)}
                                >
                                  {hasData ? formatCurrency(row.type === "income" ? displayValue : -displayValue) : "—"}
                                </td>
                              );
                            })}
                            <td className={row.type === "income" ? "positive" : "negative"}>
                              {formatCurrency(row.type === "income" ? rowTotal : -rowTotal)}
                            </td>
                            <td style={{ color: "#2c3e50", textAlign: "right", fontWeight: 600 }}>
                              {percentage.toFixed(1)}%
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="empty-state">No hay datos de categorías para este año.</div>
          )}
        </section>

      </div>
      {categoryDrawer && (
        <div className="category-drawer-container">
          <div className="category-drawer-backdrop" onClick={handleDrawerClose} aria-hidden="true" />
          <aside
            className="category-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby={drawerHeadingId}
            aria-describedby={`${drawerSubtitleId} ${drawerTotalId}`}
          >
            <button
              type="button"
              className="category-drawer-close"
              onClick={handleDrawerClose}
              aria-label="Cerrar detalle de categoría"
            >
              ×
            </button>
            <p className="category-drawer-label">
              {categoryDrawer.mode === "income" ? "Ingresos" : "Gastos"}
            </p>
            <h4 id={drawerHeadingId} className="category-drawer-title">
              <span className="category-color-label">
                <span
                  className="category-color-dot"
                  style={{ backgroundColor: categoryDrawer.categoryColor ?? DEFAULT_CATEGORY_COLOR }}
                  aria-hidden="true"
                />
                <span>{categoryDrawer.categoryName}</span>
              </span>
            </h4>
            <p id={drawerSubtitleId} className="category-drawer-subtitle">
              {MONTH_NAMES[categoryDrawer.month - 1]} · {year}
            </p>
            <p id={drawerTotalId} className="category-drawer-total">
              Total:
              <span className={`stat-value ${categoryDrawer.value >= 0 ? "positive" : "negative"}`}>
                {formatCurrency(normalizeZero(categoryDrawer.value))}
              </span>
            </p>
            {categoryDrawer.loading ? (
              <div className="category-drawer-status">
                <div className="spinner" /> Cargando movimientos...
              </div>
            ) : categoryDrawer.error ? (
              <p className="category-drawer-status negative">{categoryDrawer.error}</p>
            ) : categoryDrawer.transactions && categoryDrawer.transactions.length ? (
              <ul className="category-drawer-list">
                {categoryDrawer.transactions.map(tx => (
                  <li key={tx.id}>
                    <span className={tx.amount >= 0 ? "stat-value positive" : "stat-value negative"}>
                      {formatCurrency(tx.amount)}
                    </span>
                    <div className="category-drawer-list-body">
                      <span className="category-drawer-description">{tx.description || "Sin descripción"}</span>
                      <span className="category-drawer-date">{formatDateLabel(tx.transactionDate)}</span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="category-drawer-status">No hay movimientos registrados.</p>
            )}
          </aside>
        </div>
      )}
    </DashboardLayout>
  );
}
